import { useState, useMemo, useCallback, useRef } from 'react';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { MultiSelect } from 'primereact/multiselect';
import { InputText } from 'primereact/inputtext';
import { Button } from 'primereact/button';
import 'react-toastify/dist/ReactToastify.css';
import { useGetTrackChangeDataQuery } from './counselling.services';
import { alertService } from '../../shared/services/alertService';

/**
 * Migrated from Angular `TrackChangesComponent`
 * (src/app/views/pages/counselling/track-changes).
 *
 * Route: /counselling/track-changes
 */

// ── Column definitions ───────────────────────────────────────────────────────

interface Col {
  field: string;
  header: string;
}

const COLUMNS: Col[] = [
  { field: 'from_district_name', header: 'From District' },
  { field: 'from_udise_code', header: 'From UDISE' },
  { field: 'from_school_name', header: 'From School' },
  { field: 'Type', header: 'Type' },
  { field: 'seniority_state', header: 'Seniority' },
  { field: 'name', header: 'Teacher Name' },
  { field: 'teacher_id', header: 'Teacher Id' },
  { field: 'type_teacher', header: 'Designation' },
  { field: 'subjects', header: 'Subject' },
  { field: 'title', header: 'Counselling' },
  { field: 'to_district_name', header: 'To District' },
  { field: 'to_udise_code', header: 'To UDISE' },
  { field: 'to_school_name', header: 'To School' },
];

// ── Component ────────────────────────────────────────────────────────────────

const TrackChanges = () => {
  // UDISE input
  const [udiseCode, setUdiseCode] = useState('');
  // Submitted UDISE — drives the query
  const [submittedUdise, setSubmittedUdise] = useState<string | null>(null);

  // Column chooser
  const [selectedCols, setSelectedCols] = useState<Col[]>(COLUMNS);

  // Global filter
  const [globalFilter, setGlobalFilter] = useState('');

  const dtRef = useRef<DataTable<any[]>>(null);

  // ── Data fetching ────────────────────────────────────────────────────────

  const { data: trackRaw, isFetching: loading } = useGetTrackChangeDataQuery(
    { udiseCode: submittedUdise },
    { skip: !submittedUdise }
  );

  // ── Derived data ─────────────────────────────────────────────────────────

  const tableData = useMemo(() => {
    if (!trackRaw?.dataStatus) return [];
    return trackRaw.result;
  }, [trackRaw]);

  // ── Handlers ─────────────────────────────────────────────────────────────

  const handleSubmit = useCallback(() => {
    if (!udiseCode) {
      alertService.error('Please Enter the UDISE');
      return;
    }
    if (udiseCode.length !== 11) {
      alertService.error('Please Enter the valid UDISE');
      return;
    }
    setSubmittedUdise(udiseCode);
  }, [udiseCode]);

  // Allow Enter key to submit
  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Enter') handleSubmit();
    },
    [handleSubmit]
  );

  // ── Render ───────────────────────────────────────────────────────────────

  return (
    <div>
      <div className="card p-4">
        <div className="row align-items-end">
          <div className="col-md-3 col-6 mb-2">
            <label className="fw-bold d-block mb-1">UDISE</label>
            <InputText
              value={udiseCode}
              onChange={(e) => setUdiseCode(e.target.value.replace(/\D/g, ''))}
              onKeyDown={handleKeyDown}
              placeholder="Enter UDISE"
              maxLength={11}
              className="w-100"
              keyfilter="int"
            />
          </div>
          <div className="col-md-2 col-6 mb-2">
            <Button label="Submit" className="rounded" onClick={handleSubmit} />
          </div>
        </div>

        <div className="mt-4">
          <DataTable
            ref={dtRef}
            value={tableData}
            paginator
            rows={10}
            rowsPerPageOptions={[10, 25, 50]}
            loading={loading}
            showGridlines
            resizableColumns
            rowHover
            globalFilter={globalFilter}
            globalFilterFields={selectedCols.map((c) => c.field)}
            currentPageReportTemplate="Showing {first} to {last} of {totalRecords} entries"
            paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport RowsPerPageDropdown"
            emptyMessage="No Records Found"
            tableStyle={{ minWidth: '90rem' }}
            header={
              <div className="row">
                <div className="col-md-3 col-12 mb-2">
                  <MultiSelect
                    value={selectedCols}
                    options={COLUMNS}
                    optionLabel="header"
                    filter
                    filterBy="header"
                    onChange={(e) => {
                      const selected = COLUMNS.filter((col) =>
                        e.value.includes(col)
                      );
                      setSelectedCols(selected);
                    }}
                    selectedItemsLabel="{0} columns selected"
                    placeholder="Choose Columns"
                    className="w-100"
                  />
                </div>
                <div className="col-md-9 col-12 text-end">
                  <span className="p-input-icon-left">
                    <InputText
                      type="text"
                      value={globalFilter}
                      onChange={(e) => setGlobalFilter(e.target.value)}
                      placeholder="Search keyword"
                    />
                  </span>
                </div>
              </div>
            }
          >
            <Column
              header="#"
              body={(_data, options) => options.rowIndex + 1}
              style={{ width: '3rem' }}
            />
            {selectedCols.map((col) => (
              <Column
                key={col.field}
                field={col.field}
                header={col.header}
                sortable
                filterPlaceholder="Search"
                body={(row) => row[col.field] || '-'}
                style={{ minWidth: '8rem' }}
              />
            ))}
          </DataTable>
        </div>
      </div>
    </div>
  );
};

export default TrackChanges;
